import { serializeUIFlagListViewForAPI } from '@gonfalon/flag-filters/serializeUIFlagListViewForAPI';
import type { UIFlagListView } from '@gonfalon/flag-filters/types';
// oxlint-disable-next-line no-restricted-imports
import { fromJS, Map, OrderedMap } from '@gonfalon/immutable-shim/immutableShim';
import http, {
  jsonToImmutable,
  jsonToImmutableError,
  restApitoImmutableError,
} from '@gonfalon/legacy-http-client/httpUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';
import { type GetFeatureFlagQueryParams } from '@gonfalon/openapi/operations/getFeatureFlag';
import { type GetFeatureFlagsQueryParams } from '@gonfalon/openapi/operations/getFeatureFlags';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { updateFlag as updateFlag_ } from '@gonfalon/rest-api/mutators/updateFlag';
import { updateFlagSemantically as updateFlagSemantically_ } from '@gonfalon/rest-api/mutators/updateFlagSemantically';
import {
  getFeatureFlagQuery,
  getFeatureFlagsQuery,
  getFeatureFlagStatusAcrossEnvironmentsQuery,
  getFeatureFlagStatusesQuery,
} from '@gonfalon/rest-api/queries/flags';
import { normalize, schema } from 'normalizr';
import qs from 'qs';

import type { UpdateFlagOptions } from '#actions/flags';
import type { FlagAndEnvKeys } from '#reducers/flags';
import { convertMapToOrderedMap } from '#utils/collectionUtils';
import type { Environment } from '#utils/environments/records/Environment';
import { createBackendQueryStringFromFlagFilters } from '#utils/flagFilters/createBackendQueryStringFromFlagFilters';
import type { FlagFilters } from '#utils/flagFilters/records/FlagFilters';
import { createFlagJsonPatch } from '#utils/flagPatchUtils';
import { groupDependentFlags } from '#utils/flags/dependentFlagsHelpers';
import { createDependentFlag } from '#utils/flags/records/DependentFlags';
import type { Flag } from '#utils/flags/records/Flag';
import { createFlag } from '#utils/flags/records/Flag';
import type { FlagConfiguration } from '#utils/flags/records/FlagConfiguration';
import { createFlagConfiguration } from '#utils/flags/records/FlagConfiguration';
import type { FlagStatus } from '#utils/flagStatusUtils';
import { createFlagStatus } from '#utils/flagStatusUtils';
import type { SemanticInstruction } from '#utils/instructions/shared/types';
import type { VariationSemanticInstruction } from '#utils/instructions/variations/types';
import type { Link } from '#utils/linkUtils';
const flags = new schema.Entity('flags', {}, { idAttribute: 'key' });

// TODO: flag for v2
const statusId = (s: { _links: { parent: { href: string } } }) => s._links.parent.href;

const statuses = new schema.Entity('statuses', {}, { idAttribute: statusId });

type FlagFilterQueryObject = {
  summary?: number;
  archived?: boolean;
  compare?: boolean;
  expand?: 'codereferences';
  [key: string]: number | boolean | string | undefined;
};

type FlagFilterToQueryStringType = {
  [flagFilter: string]: { key: string; valueMap(value?: boolean): number | boolean | string | undefined };
};

const flagFilterToQueryString: FlagFilterToQueryStringType = {
  fullListing: {
    key: 'summary',
    valueMap: (v: boolean) => (v ? 0 : 1),
  },
  showArchived: {
    key: 'archived',
    valueMap: (v: boolean) => (v ? true : undefined),
  },
  compare: {
    key: 'compare',
    valueMap: (v: boolean) => (v ? 1 : undefined),
  },
  withCodeRefs: {
    key: 'expand',
    valueMap: (v: boolean) => (v ? 'codeReferences' : undefined),
  },
};

export const stringifyFetchFlagsOptions = (options: FetchFlagsOptions) => {
  const qsObject: FlagFilterQueryObject = {};

  Object.keys(options).forEach((o: string) => {
    const mapping = flagFilterToQueryString[o];
    if (mapping) {
      const { key, valueMap } = mapping;
      qsObject[key] = valueMap(options[o]);
    }
  });
  return qs.stringify(qsObject);
};

export const stringifyEnvKeys = (envKeys: string[]) => {
  const sanitizedEnvKeys = envKeys.filter((k) => !!k);
  return qs.stringify({ env: sanitizedEnvKeys }, { indices: false });
};

export const createGetFlagsQueryString = (
  environmentKeys?: string[],
  options: FetchFlagsOptions = {},
  filters?: FlagFilters,
) => {
  if (environmentKeys?.length) {
    const envKeysQuery = stringifyEnvKeys(environmentKeys);
    const optionsQuery = stringifyFetchFlagsOptions(options);
    const filtersQuery = filters ? createBackendQueryStringFromFlagFilters(filters) : undefined;

    const result = [envKeysQuery, optionsQuery, filtersQuery].filter((s) => !!s).join('&');
    return result ? `?${result}` : result;
  }

  return '';
};

export type FlagsResponse = ImmutableMap<{
  entities: ImmutableMap<{
    flags: OrderedMap<string, Flag>;
  }>;
  result: ImmutableMap<{
    _links: ImmutableMap<{ self: Link }>;
    items: string[];
  }>;
}>;

export type FetchFlagsRequestOptions = {
  controller?: AbortController;
  signal?: AbortSignal;
};

export type FetchFlagsOptions = {
  [option: string]: boolean | undefined;
  fullListing?: boolean;
  showArchived?: boolean;
  refetch?: boolean;
  compare?: boolean;
  withCodeRefs?: boolean;
};

function getFlags(
  projectKey: string,
  environmentKeys?: string[],
  filters?: FlagFilters,
  options: FetchFlagsOptions = {},
): Promise<FlagsResponse> {
  const coderefs = filters?.codeReferences?.toJS();
  const ui: UIFlagListView = {
    summary: !options.fullListing,
    archived: options.showArchived || undefined,
    compare: options.compare || undefined,
    limit: filters?.limit,
    offset: filters?.offset,
    sort: filters?.sort as GetFeatureFlagsQueryParams['sort'],
    filter: {
      tags: filters?.tags.toArray(),
      created: filters?.creationDate,
      evaluated: filters?.evaluated,
      excludeSettings: filters?.settings.toArray(),
      filterEnv: filters?.filterEnv,
      hasExperiment: filters?.hasExperiment,
      maintainerId: filters?.maintainerId,
      maintainerTeamKey: filters?.maintainerTeamKey,
      query: filters?.q,
      type: filters?.type,
      segmentTargeted: filters?.segmentTargeted,
      applicationEvaluated: filters?.applicationEvaluated,
      state: filters?.state,
      status: filters?.status as NonNullable<GetFeatureFlagsQueryParams['filter']>['status'],
      sdkAvailability: filters?.sdkAvailability as NonNullable<GetFeatureFlagsQueryParams['filter']>['sdkAvailability'],
      // Deprecated filters (v4 of the API) - omitDisabledFilters in serializeUIFlagListViewForAPI handles cleanup
      hasDataExport: filters?.hasDataExport,
      targeting: filters?.targeting as NonNullable<GetFeatureFlagsQueryParams['filter']>['targeting'],
      contextKindsEvaluated: filters?.contextKindsEvaluated?.toArray(),
      contextKindTargeted: filters?.contextKindTargeted,
      followerId: filters?.followerId,
    },
  };

  if (coderefs !== undefined) {
    ui.filter = ui.filter ?? {};
    ui.filter.codeReferences = 'min' in coderefs && coderefs.min !== undefined ? coderefs.min === 1 : false;
  }

  const api = serializeUIFlagListViewForAPI(ui);
  api.env = environmentKeys;
  return getQueryClient()
    .fetchQuery(getFeatureFlagsQuery({ projectKey, query: api }))
    .then((res) => {
      const data = normalize(res, { items: [flags] });
      return fromJS(data).withMutations((map: Map<string, Flag>) => {
        map.updateIn(['entities', 'flags'], (f: Map<string, Flag>) => convertMapToOrderedMap(f, data.result.items));
        map.updateIn(['entities', 'flags'], (flgs: Map<string, Flag>) => (flgs ? flgs.map(createFlag) : OrderedMap()));
      });
    })
    .catch(restApitoImmutableError);
}

export type FetchFlagByKeyOptions = {
  expandEvaluation?: boolean;
};

function getFlagByKey(
  projectKey: string,
  flagKey: string,
  environmentKeys: string[],
  options?: FetchFlagByKeyOptions,
): Promise<Flag> {
  const expansions: GetFeatureFlagQueryParams['expand'] = [];

  if (options?.expandEvaluation) {
    expansions.push('evaluation');
  }

  const queryClient = getQueryClient();

  return queryClient
    .fetchQuery(
      getFeatureFlagQuery({
        projectKey,
        featureFlagKey: flagKey,
        query: {
          env: environmentKeys,
          expand: expansions,
        },
      }),
    )
    .then((data) => fromJS(data))
    .then(createFlag)
    .catch(restApitoImmutableError);
}

function getDependentFlags(projectKey: string, flagKey: string): Promise<FlagAndEnvKeys> {
  const url = `/api/v2/flags/${projectKey}/${flagKey}/dependent-flags`;
  return http
    .get(url, {
      headers: { 'LD-API-Version': 'beta' },
    })
    .then(jsonToImmutable)
    .then((data) => createDependentFlag(data))
    .then((data) => groupDependentFlags(data))
    .catch(jsonToImmutableError);
}

function getAllFlagStatusesForEnvironment(
  projectKey: string,
  environmentKey: string,
): Promise<Map<string, FlagStatus>> {
  const url = `/api/v2/flag-statuses/${projectKey}/${environmentKey}`;
  return http
    .get(url)
    .then((res) =>
      res.json().then((jsn) => {
        const data = normalize(jsn, { items: [statuses] });
        return fromJS(data).updateIn(['entities', 'statuses'], (ss: Map<string, FlagStatus>) =>
          ss ? ss.map(createFlagStatus) : Map(),
        );
      }),
    )
    .catch(jsonToImmutableError);
}

function getFlagStatusesByEnvKeys(
  projKey: string,
  flagKey: string,
  environmentKeys: string[],
): Promise<Map<string, FlagStatus>> {
  const queryClient = getQueryClient();

  if (environmentKeys.length === 0) {
    return queryClient
      .fetchQuery(getFeatureFlagStatusAcrossEnvironmentsQuery({ projectKey: projKey, featureFlagKey: flagKey }))
      .then((data) => fromJS(data))
      .then((entities) => entities.get('environments').map(createFlagStatus))
      .catch(restApitoImmutableError);
  }

  return queryClient
    .fetchQuery(getFeatureFlagStatusesQuery({ projectKey: projKey, flagKey, environmentKeys }))
    .then((data) => fromJS(data))
    .then((entities) => entities.map(createFlagStatus))
    .catch(restApitoImmutableError);
}

function postFlag(feature: Flag, projectKey: string, options: { clonedFlagKey?: string } = {}): Promise<Flag> {
  const rep = feature.toGlobalRep();
  const versionUrl = `/api/v2/flags/${projectKey}`;
  const url = options.clonedFlagKey ? `${versionUrl}?clone=${options.clonedFlagKey}` : versionUrl;

  return http.post(url, { body: rep }).then(jsonToImmutable).then(createFlag).catch(jsonToImmutableError);
}

function updateFlag(projectKey: string, oldFlag: Flag, newFlag: Flag, options: UpdateFlagOptions = {}): Promise<Flag> {
  const { comment = '', patchOperations } = options;
  const patch =
    patchOperations ||
    createFlagJsonPatch(oldFlag, newFlag, comment, { removeEmptyFields: options?.removeEmptyFields });

  return updateFlag_({ projectKey, flagKey: oldFlag.key, comment, patch: Array.isArray(patch) ? patch : patch.patch })
    .then((data) => fromJS(data))
    .then(createFlag)
    .catch(restApitoImmutableError);
}

function deleteFlag(flag: Flag): Promise<void> {
  return http.delete(flag.selfLink()).catch(jsonToImmutableError);
}

function postFlagDebug(flag: Flag, environment: Environment, timeInSeconds: number): Promise<FlagConfiguration> {
  return http
    .post(`/internal/account/environments/${environment._id}/flags/${flag.key}/debug`, {
      body: { debugEventsSeconds: timeInSeconds || 0 },
    })
    .then(jsonToImmutable)
    .then(createFlagConfiguration)
    .catch(jsonToImmutableError);
}

function copyFlagConfig(
  flagkey: string,
  projectKey: string,
  targetEnvironmentKey: string,
  sourceEnvironmentKey: string,
  options: {
    comment?: string;
    includedActions?: string[];
    excludedActions?: string[];
  },
): Promise<Flag> {
  return http
    .post(`/api/v2/flags/${projectKey}/${flagkey}/copy`, {
      body: {
        source: {
          key: sourceEnvironmentKey,
        },
        target: {
          key: targetEnvironmentKey,
        },
        comment: options.comment,
        includedActions: options.includedActions,
        excludedActions: options.excludedActions,
      },
    })
    .then(jsonToImmutable)
    .then(createFlag)
    .catch(jsonToImmutableError);
}

function updateFlagSemantically(
  projectKey: string,
  flag: Flag,
  environmentKey: string,
  instructions: Iterable<SemanticInstruction | VariationSemanticInstruction>,
  options: { comment?: string } = {},
): Promise<Flag> {
  const { comment = '' } = options;

  return updateFlagSemantically_({
    projectKey,
    flagKey: flag.key,
    ignoreConflicts: true,
    environmentKey,
    instructions: Array.from(instructions),
    comment,
  })
    .then((data) => fromJS(data))
    .then((flagResp) => {
      const modifiedFlag = createFlag(flagResp);
      return modifiedFlag.preserveFlagContextsEvaluation(flag);
    })
    .catch(restApitoImmutableError);
}

export {
  getFlags,
  getFlagByKey,
  getAllFlagStatusesForEnvironment,
  getFlagStatusesByEnvKeys,
  postFlag,
  updateFlag,
  updateFlagSemantically,
  deleteFlag,
  getDependentFlags,
  postFlagDebug,
  copyFlagConfig,
};
